import { useEffect } from 'preact/hooks';
import { isAuthed } from '@/store/auth';
import { currentRoute, navigate } from '@/router';
import { loadDashboard, loadNews } from '@/store/dashboard';
import { loadCategories } from '@/store/catalog';
import { cartCount } from '@/store/cart';
import { closeTopModal, drawerOpen, closeDrawer } from '@/store/ui';
import { isMobile } from '@/lib/viewport';

import { AuthScreen } from '@/features/auth/AuthScreen.jsx';
import { Sidebar } from '@/features/shell/Sidebar.jsx';
import { Header } from '@/features/shell/Header.jsx';
import { MobileTopBar } from '@/features/shell/MobileTopBar.jsx';
import { MobileBottomNav } from '@/features/shell/MobileBottomNav.jsx';
import { PullToRefresh } from '@/features/shell/PullToRefresh.jsx';
import { refreshCurrentRoute } from '@/lib/refresh';
import { initPush } from '@/pwa/push';
import { Dashboard } from '@/features/dashboard/Dashboard.jsx';
import { OrdersList } from '@/features/orders/OrdersList.jsx';
import { CatalogPage } from '@/features/catalog/CatalogPage.jsx';
import { CartPage } from '@/features/cart/CartPage.jsx';
import { Stub } from '@/features/stub/Stub.jsx';
import { NotificationStack } from '@/ui/NotificationStack.jsx';
import { InstallBanner } from '@/features/pwa/InstallBanner.jsx';
import { IosInstallModal } from '@/features/pwa/IosInstallModal.jsx';

export function App() {
  useEffect(() => {
    // Esc — закрыть верхнюю модалку. Иначе — игнор.
    function onKey(e) {
      if (e.key === 'Escape') closeTopModal();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!isAuthed.value) return;
    // initial-load после логина.
    loadDashboard();
    loadNews();
    loadCategories();
    initPush(); // определить текущее состояние push-подписки
  }, [isAuthed.value]);

  // Подсветка favicon-бейджа: title с количеством товаров.
  useEffect(() => {
    const count = cartCount.value;
    document.title = count > 0
      ? `(${count}) B2B Кондор-Юг`
      : 'B2B Кондор-Юг — Личный кабинет';
  });

  const mobile = isMobile.value;
  const drawer = drawerOpen.value;

  // Блокируем скролл body при открытом drawer (нативное поведение).
  useEffect(() => {
    if (mobile && drawer) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobile, drawer]);

  // При закрытии drawer на переходе в десктоп — сбрасываем состояние.
  useEffect(() => {
    if (!mobile && drawer) closeDrawer();
  }, [mobile, drawer]);

  // Swipe-back: жест от левого края экрана → назад по истории (нативный паттерн iOS/Android).
  useEffect(() => {
    if (!mobile) return undefined;
    let sx = 0;
    let sy = 0;
    let tracking = false;

    function onStart(e) {
      const t = e.touches[0];
      // Старт только у самого левого края и при закрытом drawer.
      if (!drawerOpen.value && t.clientX <= 24) {
        sx = t.clientX;
        sy = t.clientY;
        tracking = true;
      } else {
        tracking = false;
      }
    }
    function onEnd(e) {
      if (!tracking) return;
      tracking = false;
      const t = e.changedTouches[0];
      const dx = t.clientX - sx;
      const dy = Math.abs(t.clientY - sy);
      // Достаточный горизонтальный сдвиг, преимущественно горизонтальный.
      if (dx > 70 && dy < 50 && window.history.length > 1) {
        window.history.back();
      }
    }
    document.addEventListener('touchstart', onStart, { passive: true });
    document.addEventListener('touchend', onEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onStart);
      document.removeEventListener('touchend', onEnd);
    };
  }, [mobile]);

  if (!isAuthed.value) {
    return (
      <>
        <AuthScreen />
        <NotificationStack />
      </>
    );
  }

  return (
    <div class={`app-shell ${mobile ? 'is-mobile' : ''} ${mobile && drawer ? 'drawer-open' : ''}`}>
      {mobile && <MobileTopBar />}
      <Sidebar />
      {mobile && (
        <div
          class={`drawer-backdrop ${drawer ? 'is-open' : ''}`}
          onClick={closeDrawer}
          aria-hidden="true"
        />
      )}
      <main class="content">
        {!mobile && <Header />}
        <InstallBanner />
        {mobile ? (
          <PullToRefresh onRefresh={refreshCurrentRoute}>
            <div class="page" key={currentRoute.value}>
              <CurrentView />
            </div>
          </PullToRefresh>
        ) : (
          <CurrentView />
        )}
      </main>
      {mobile && <MobileBottomNav />}
      <NotificationStack />
      <IosInstallModal />
    </div>
  );
}

function CurrentView() {
  const route = currentRoute.value;
  switch (route) {
    case 'dashboard':
      return <Dashboard />;
    case 'orders':
      return <OrdersList />;
    case 'search':
      return <CatalogPage />;
    case 'cart':
      return <CartPage />;
    case 'promotions':
      return <Stub text="Раздел «Акции» появится после интеграции." />;
    case 'useful':
      return <Stub text="Раздел «Полезное» будет наполнен позже." />;
    case 'about':
      return <Stub text="Страницы «Кондор-Юг» в разработке." />;
    default:
      navigate('dashboard');
      return null;
  }
}
